import { Cubit } from "blac-next";
import FileType from "src/constants/fileType";
import { globalEvents } from "src/constants/globalEvents";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { fileState, toast } from "src/state/state";

import {
  AddMealInsightFeedbackRequest,
  CreateMealInsightResponse,
  MealInsightControllerService
} from "@9amhealth/openapi";

interface MealInsightState {
  loading: boolean;
  fileId?: string;
  mealInsight?: CreateMealInsightResponse;
  error?: Error;
  imageError?: Error;
  uploadProgress?: {
    loaded: number;
    total: number;
    percentage: number;
    phase: "uploading" | "analyzing";
  };
  previewUrl?: string;
}

const buildInitialState = (): MealInsightState => ({
  loading: false
});

class MealInsightBloc extends Cubit<MealInsightState> {
  static keepAlive = true;

  private operationGeneration = 0;
  private progressInterval?: ReturnType<typeof setInterval>;

  constructor() {
    super(buildInitialState());

    // keepAlive keeps this cubit (and its blob previewUrl / fileId) alive across
    // logout. Reset it when the user is cleared or switched so the next user
    // doesn't see the previous user's meal photo/insight.
    window.addEventListener(globalEvents.USER_CLEAR, this.reset);
    window.addEventListener(globalEvents.USER_CHANGED, this.reset);
  }

  private readonly reset = (): void => {
    // Bump the generation so any in-flight upload / insight generation started
    // for the previous user is invalidated and cannot patch state or navigate
    // after the switch (see the generation checks in the async flows below).
    this.operationGeneration += 1;
    this.clearProgressInterval();
    this.revokePreviewUrl();
    this.emit(buildInitialState());
  };

  private readonly clearProgressInterval = (): void => {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }
  };

  private readonly revokePreviewUrl = (): void => {
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
  };

  uploadPhoto = async (file: File): Promise<string | undefined> => {
    const generation = this.operationGeneration;

    // Revoke any previous preview so repeated uploads don't leak blob URLs.
    this.revokePreviewUrl();
    const previewUrl = URL.createObjectURL(file);

    this.patch({
      loading: true,
      imageError: undefined,
      previewUrl,
      uploadProgress: {
        loaded: 0,
        total: file.size,
        percentage: 0,
        phase: "uploading"
      }
    });

    try {
      const upload = await fileState.uploadFileWithProgress(
        {
          file,
          fileAttributes: {
            "file.type": FileType.MEAL_INSIGHTS
          }
        },
        false,
        {
          onProgress: (loaded, total, percentage) => {
            if (generation !== this.operationGeneration) {
              return;
            }
            this.patch({
              uploadProgress: {
                loaded,
                total,
                percentage,
                phase: "uploading"
              }
            });
          }
        }
      );

      // A user switch during the upload invalidates this operation; don't patch
      // the previous user's file onto the reset user.
      if (generation !== this.operationGeneration) {
        return;
      }

      if (!upload) {
        throw new Error("Failed to upload file");
      }

      this.patch({
        loading: false,
        fileId: upload.id,
        uploadProgress: {
          loaded: 0,
          total: 100,
          percentage: 0,
          phase: "analyzing"
        }
      });

      return upload.id;
    } catch (error: unknown) {
      if (generation !== this.operationGeneration) {
        return;
      }
      this.patch({
        imageError: error as Error,
        uploadProgress: undefined
      });
      reportErrorSentry(error);
    } finally {
      if (generation === this.operationGeneration) {
        this.patch({ loading: false });
      }
    }
  };

  handleUploadPhoto = async (
    file: File,
    navigate: (path: string, options?: { replace?: boolean }) => void,
    origin: "home" | "meals" = "home"
  ): Promise<void> => {
    const generation = this.operationGeneration;
    const query = `?from=${origin}`;
    const tempPath = `${createAppPath(Path.mealInsights)}/${Path.tmp}${query}`;
    navigate(tempPath);

    const fileId = await this.uploadPhoto(file);

    // Bail if a user switch happened during the upload so we don't navigate the
    // reset user to the previous user's insight.
    if (generation !== this.operationGeneration) {
      return;
    }

    if (fileId) {
      const targetUrl = `${createAppPath(Path.mealInsights)}/${fileId}${query}`;
      navigate(targetUrl, {
        replace: true
      });
    }
  };

  clearMealInsight = () => {
    this.patch({
      loading: false,
      mealInsight: undefined,
      error: undefined
    });
  };

  generateMealInsight = async (fileId: string) => {
    const generation = this.operationGeneration;

    this.clearMealInsight();
    this.clearProgressInterval();

    try {
      this.patch({
        loading: true,
        uploadProgress: {
          loaded: 0,
          total: 100,
          percentage: 0,
          phase: "analyzing"
        }
      });

      const avgLoadTimeMs = 8000;
      const startTime = Date.now();

      this.progressInterval = setInterval(() => {
        // Stop simulating progress if a user switch invalidated this operation.
        if (generation !== this.operationGeneration) {
          this.clearProgressInterval();
          return;
        }
        const current = this.state.uploadProgress?.percentage ?? 0;
        if (current < 90) {
          const elapsed = Date.now() - startTime;
          const simulatedPercentage = Math.min(
            (elapsed / avgLoadTimeMs) * 90,
            90
          );
          this.patch({
            uploadProgress: {
              loaded: simulatedPercentage,
              total: 100,
              percentage: simulatedPercentage,
              phase: "analyzing"
            }
          });
        }
      }, 100);

      const response = await MealInsightControllerService.createMealInsight({
        fileId
      });

      this.clearProgressInterval();

      // Don't surface the previous user's insight on the reset user.
      if (generation !== this.operationGeneration) {
        return;
      }

      this.patch({
        loading: false,
        mealInsight: response.data,
        uploadProgress: undefined
      });
    } catch (error: unknown) {
      this.clearProgressInterval();

      if (generation !== this.operationGeneration) {
        return;
      }

      toast.error("mealInsights.error");
      this.patch({
        loading: false,
        error: error as Error,
        uploadProgress: undefined
      });
      reportErrorSentry(error);
    }
  };

  discardMealInsight = async (): Promise<boolean> => {
    const mealInsightId = this.state.mealInsight?.id;
    if (!mealInsightId) {
      return false;
    }

    try {
      await MealInsightControllerService.deleteMealInsight(mealInsightId);
      this.clearMealInsight();
      return true;
    } catch (error: unknown) {
      toast.error("mealInsights.error");
      reportErrorSentry(error);
      return false;
    }
  };

  rateMealInsight = async (rating: AddMealInsightFeedbackRequest.rating) => {
    const mealInsightId = this.state.mealInsight?.id;
    if (!mealInsightId) {
      throw new Error("Meal insight ID is required");
    }

    try {
      await MealInsightControllerService.addMealInsightFeedback(mealInsightId, {
        rating
      });
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };
}

export default MealInsightBloc;
